/**
 * Error Boundary and Global Error Handler
 *
 * Catches and logs JavaScript errors for better debugging and user experience
 *
 * @package pixelkicks Child
 * @since 3.1.0
 */

/**
 * Global error handler
 */
window.addEventListener("error", (event) => {
  console.error("Global error:", {
    message: event.message,
    filename: event.filename,
    lineno: event.lineno,
    colno: event.colno,
    error: event.error,
  });

  // Send to logging endpoint (optional)
  if (
    typeof ajax_variable !== "undefined" &&
    !window.location.hostname.includes("localhost")
  ) {
    navigator.sendBeacon(
      ajax_variable.ajax_url + "?action=log_js_error",
      JSON.stringify({
        message: event.message,
        url: window.location.href,
        filename: event.filename,
        line: event.lineno,
        column: event.colno,
        stack: event.error?.stack,
        userAgent: navigator.userAgent,
      }),
    );
  }

  // Show user-friendly error in development
  if (
    window.location.hostname === "localhost" ||
    window.location.hostname.includes("local")
  ) {
    showErrorNotification(event.message);
  }
});

/**
 * Unhandled promise rejection handler
 */
window.addEventListener("unhandledrejection", (event) => {
  console.error("Unhandled promise rejection:", event.reason);

  // Log to server
  if (
    typeof ajax_variable !== "undefined" &&
    !window.location.hostname.includes("localhost")
  ) {
    navigator.sendBeacon(
      ajax_variable.ajax_url + "?action=log_js_error",
      JSON.stringify({
        message: "Unhandled Promise Rejection: " + event.reason,
        url: window.location.href,
        userAgent: navigator.userAgent,
      }),
    );
  }
});

/**
 * Show user-friendly error notification (development only)
 * @param {string} message - Error message
 */
function showErrorNotification(message) {
  const notification = document.createElement("div");
  notification.className = "dev-error-notification";
  notification.style.cssText = `
    position: fixed;
    bottom: 20px;
    right: 20px;
    background: #ff4444;
    color: white;
    padding: 15px 20px;
    border-radius: 4px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.2);
    z-index: 999999;
    max-width: 400px;
    font-family: monospace;
    font-size: 12px;
  `;
  notification.textContent = "JS Error: " + message;

  document.body.appendChild(notification);

  // Auto-remove after 5 seconds
  setTimeout(() => {
    notification.remove();
  }, 5000);
}

/**
 * Safe function wrapper with error handling
 * @param {Function} fn - Function to wrap
 * @param {string} context - Context description for logging
 * @returns {Function} Wrapped function
 */
export function safeExecute(fn, context = "unknown") {
  return function (...args) {
    try {
      return fn.apply(this, args);
    } catch (error) {
      console.error(`Error in ${context}:`, error);
      return null;
    }
  };
}

/**
 * Async safe function wrapper
 * @param {Function} fn - Async function to wrap
 * @param {string} context - Context description
 * @returns {Function} Wrapped async function
 */
export function safeExecuteAsync(fn, context = "unknown") {
  return async function (...args) {
    try {
      return await fn.apply(this, args);
    } catch (error) {
      console.error(`Error in ${context}:`, error);
      return null;
    }
  };
}

/**
 * Retry failed operations
 * @param {Function} fn - Function to retry
 * @param {number} maxRetries - Maximum retry attempts
 * @param {number} delay - Delay between retries (ms)
 * @returns {Promise}
 */
export async function retryOperation(fn, maxRetries = 3, delay = 1000) {
  let lastError;

  for (let i = 0; i < maxRetries; i++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      if (i < maxRetries - 1) {
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }
  }

  throw lastError;
}

/**
 * Feature detection with fallback
 * @param {Function} test - Feature test function
 * @param {Function} fallback - Fallback function if feature not supported
 */
export function withFallback(test, fallback) {
  try {
    if (test()) {
      return true;
    }
    fallback();
    return false;
  } catch (error) {
    console.warn("Feature detection failed:", error);
    fallback();
    return false;
  }
}

export default {
  safeExecute,
  safeExecuteAsync,
  retryOperation,
  withFallback,
};
